import React, { useState, useEffect, useMemo } from 'react';
import { 
  Typography, 
  Card, 
  Button, 
  Form, 
  Upload, 
  message, 
  Spin, 
  Row, 
  Col, 
  Checkbox, 
  Alert,
  Space,
  Tag,
  Modal,
  Table,
  Select,
  Tooltip,
  Tabs
} from 'antd';
import ThermoMPNNResults from '../../../components/results/structure/ThermoMPNNResults';
import { useNavigate } from 'react-router-dom';
import { 
  ExperimentOutlined, 
  ArrowLeftOutlined, 
  InboxOutlined,
  InfoCircleOutlined,
  PlayCircleOutlined,
  SyncOutlined,
  DeleteOutlined,
  StopOutlined,
  EyeOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import MolStarViewer from '../../../components/results/protein/structure/MolStarViewer';
import session from '../../../utils/session';
import { createTask, getTasksWithCurrent, cancelTask, TaskResponse } from '../../../utils/taskManager';
import { shouldDisplayInList } from '../../../utils/taskManager';

const { Title, Paragraph, Text } = Typography;
const { Dragger } = Upload;

interface ChainInfo {
  chainId: string;
  residueCount: number;
}

interface StructureInfo {
  chains: string[];
  chainDetails?: ChainInfo[];
  filename?: string;
  sequences?: { [chainId: string]: string };
  residueNumbers?: { [chainId: string]: number[] };  // 序列索引 -> PDB残基编号映射
}

interface MutationData {
  position: number;
  wildtype: string;
  mutation: string;
  ddG_pred: number;
  category: string;
  mutation_name?: string;
}

interface ThermoMPNNTaskResult {
  task_id?: string;
  success?: boolean;
  summary?: {
    total_mutations: number;
    mean_ddG: number;
    std_ddG: number;
    stabilizing_count: number;
    neutral_count: number;
    destabilizing_count: number;
    best_mutation: MutationData;
    worst_mutation: MutationData;
  };
  mutations?: MutationData[];
  position_analysis?: Array<{
    position: number;
    wildtype: string;
    mean_ddG: number;
    min_ddG: number;
    max_ddG: number;
    std_ddG: number;
    best_mutation: string;
    best_ddG: number;
  }>;
  raw_csv_data?: any[];
  files?: {
    input_pdb: string;
    output_csv: string;
    task_directory: string;
  };
}

export default function ThermoMPNNPage() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  
  // 状态管理
  const [file, setFile] = useState<File | null>(null);
  const [pdbData, setPdbData] = useState<string | null>(null);
  const [structureInfo, setStructureInfo] = useState<StructureInfo | null>(null);
  const [selectedChain, setSelectedChain] = useState<string>('A');
  const [includeCys, setIncludeCys] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tasks, setTasks] = useState<TaskResponse[]>([]);
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const [currentTaskResult, setCurrentTaskResult] = useState<ThermoMPNNTaskResult | null>(null);
  const [viewerVisible, setViewerVisible] = useState<boolean>(false);

  const sessionId = useMemo(() => session.getSessionId(), []);

  // 组件挂载时自动刷新任务列表
  useEffect(() => {
    refreshTasks();
    const interval = setInterval(refreshTasks, 10000);
    return () => clearInterval(interval);
  }, []);

  // 处理文件上传
  const handleFileChange = (info: any) => {
    if (info.file.status === 'removed') { 
      setFile(null); 
      setPdbData(null);
      setStructureInfo(null);
      setSelectedChain('A');
      return; 
    }
    const fileObj = info.file.originFileObj || (info.file.status !== 'uploading' ? info.file : null);
    if (fileObj instanceof File) {
      setFile(fileObj);
      
      const loadingMessage = message.loading({ content: '正在解析PDB结构文件...', duration: 0 });
      
      // 读取文件内容
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target && event.target.result) {
          const pdbContent = event.target.result.toString();
          setPdbData(pdbContent);
          
          // 从PDB文件内容中识别实际的链和序列
          const chains = extractChainsFromPDB(pdbContent);
          const { sequences, residueNumbers } = extractSequencesFromPDB(pdbContent);
          const info: StructureInfo = {
            chains: chains,
            sequences: sequences,
            residueNumbers: residueNumbers,
            filename: fileObj.name.replace(/\.[^/.]+$/, "")
          };
          setStructureInfo(info);
          setSelectedChain(chains[0] || 'A');
          
          loadingMessage();
          message.success(`识别到${chains.length}个链: ${chains.join(', ')}`);
        }
      };
      reader.onerror = () => {
        loadingMessage();
        message.error('读取PDB文件失败');
      };
      reader.readAsText(fileObj);
    }
  };

  // 刷新任务列表
  const refreshTasks = async () => {
    try {
      const list = await getTasksWithCurrent(0, 50, undefined, 'structure_design', 'thermompnn');
      console.log('获取到的ThermoMPNN任务列表:', list);
      
      // 过滤掉已删除的任务
      const deletedTaskIds = JSON.parse(localStorage.getItem('deletedTaskIds') || '[]');
      const filteredList = list.filter(task => !deletedTaskIds.includes(task.id));
      
      // 接入 shouldDisplayInList，仅展示应显示在列表中的长时任务
      const displayList = filteredList.filter(shouldDisplayInList);
      
      // 如果过滤后为空，直接显示所有未删除的任务
      if (displayList.length === 0 && filteredList.length > 0) {
        setTasks(filteredList);
      } else {
        setTasks(displayList);
      }
    } catch (e) {
      console.error('获取任务列表失败:', e);
    }
  };

  // 提交任务
  const submitTask = async () => {
    if (!file) { 
      message.error('请上传PDB文件'); 
      return; 
    }

    try {
      setSubmitting(true);
      setError(null);

      // 将文件读取为文本内容
      const toText = (file: File) => new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = reject;
        reader.readAsText(file);
      });

      const pdbContent = await toText(file);

      const task = await createTask({
        name: 'ThermoMPNN 稳定性预测',
        description: 'ThermoMPNN protein stability prediction',
        service_name: 'structure_design',
        task_type: 'thermompnn',
        handler_name: 'thermompnn_predict_json',
        parameters: {
          pdb_content: pdbContent,
          chain: selectedChain,
          include_cys: includeCys
        },
        priority: 5,
      });

      message.success(`任务已提交：${task.id}`);
      await refreshTasks();
    } catch (e: any) {
      setError(e?.message || '提交任务失败');
      message.error(`提交任务失败: ${e?.message || '未知错误'}`);
    } finally {
      setSubmitting(false);
    }
  };

  // 任务表格列定义
  const columns = [
    { 
      title: '选择', 
      key: 'selection', 
      width: 60,
      render: (_: any, rec: TaskResponse) => (
        <Checkbox 
          checked={selectedTaskIds.includes(rec.id)}
          onChange={(e) => handleSelectTask(rec.id, e.target.checked)}
          disabled={rec.status === 'running' || rec.status === 'pending'}
        />
      )
    },
    { title: '任务ID', dataIndex: 'id', key: 'id', width: 280, ellipsis: true },
    { title: '状态', dataIndex: 'status', key: 'status', width: 120, render: (s: string) => <Tag color={s === 'completed' ? 'green' : s === 'failed' ? 'red' : 'blue'}>{s}</Tag> },
    { title: '进度', dataIndex: 'progress', key: 'progress', width: 100 },
    { title: '更新时间', dataIndex: 'updated_at', key: 'updated_at', width: 200 },
    { title: '操作', key: 'action', render: (_: any, rec: TaskResponse) => {
      // ThermoMPNN结果检测逻辑
      const hasResult = rec.status === 'completed' && rec.result && (
        (rec.result.success === true && rec.result.mutations && Array.isArray(rec.result.mutations)) ||
        (rec.result.results && Array.isArray(rec.result.results) && rec.result.results.length > 0)
      );
      const canDelete = rec.status !== 'running' && rec.status !== 'pending';
      const canCancel = rec.status === 'running' || rec.status === 'pending' || rec.status === 'queued';
      
      return (
        <Space>
          <Button size="small" onClick={() => showResults(rec)} disabled={!hasResult}>查看结果</Button>
          {canCancel && (
            <Button 
              size="small" 
              type="default"
              style={{ color: '#fa8c16', borderColor: '#fa8c16' }}
              icon={<StopOutlined />} 
              onClick={() => handleCancelTask(rec.id)}
            >
              取消任务
            </Button>
          )}
          {canDelete && (
            <Button 
              size="small" 
              danger 
              icon={<DeleteOutlined />} 
              onClick={() => handleDeleteTask(rec.id)}
            >
              删除
            </Button>
          )}
        </Space>
      );
    }},
  ];

  // 显示结果
  const showResults = (task: TaskResponse) => {
    console.log('查看ThermoMPNN任务结果:', task);
    const res: ThermoMPNNTaskResult | undefined = (task.result as any) as ThermoMPNNTaskResult;
    
    setCurrentTaskResult(res || null);
    setViewerVisible(true);
  };

  // 从PDB文件内容中提取链信息
  const extractChainsFromPDB = (pdbContent: string): string[] => {
    const chains = new Set<string>();
    const lines = pdbContent.split('\n');
    
    for (const line of lines) {
      if (line.startsWith('ATOM  ') || line.startsWith('HETATM')) {
        const chainId = line.charAt(21);
        if (chainId && chainId.trim() !== '') {
          chains.add(chainId.trim());
        }
      }
    }
    
    const chainArray = Array.from(chains).sort();
    return chainArray.length > 0 ? chainArray : ['A'];
  };

  // 从PDB文件中提取序列信息和残基编号映射
  const extractSequencesFromPDB = (pdbContent: string): { 
    sequences: { [chainId: string]: string };
    residueNumbers: { [chainId: string]: number[] };
  } => {
    const sequences: { [chainId: string]: string } = {};
    const residueNumbers: { [chainId: string]: number[] } = {};
    const lines = pdbContent.split('\n');
    const chainResidues: { [chainId: string]: { [resNum: string]: string } } = {};
    
    const aaMap: { [key: string]: string } = {
      'ALA': 'A', 'ARG': 'R', 'ASN': 'N', 'ASP': 'D', 'CYS': 'C',
      'GLU': 'E', 'GLN': 'Q', 'GLY': 'G', 'HIS': 'H', 'ILE': 'I',
      'LEU': 'L', 'LYS': 'K', 'MET': 'M', 'PHE': 'F', 'PRO': 'P',
      'SER': 'S', 'THR': 'T', 'TRP': 'W', 'TYR': 'Y', 'VAL': 'V'
    };
    
    for (const line of lines) {
      if (line.startsWith('ATOM  ')) {
        const chainId = line.charAt(21).trim();
        const resName = line.substring(17, 20).trim();
        const resNum = line.substring(22, 26).trim();
        const atomName = line.substring(12, 16).trim();
        
        if (atomName === 'CA' && aaMap[resName]) {
          if (!chainResidues[chainId]) {
            chainResidues[chainId] = {};
          }
          chainResidues[chainId][resNum] = aaMap[resName];
        }
      }
    }
    
    for (const chainId in chainResidues) {
      const residues = chainResidues[chainId];
      const sortedResNums = Object.keys(residues).sort((a, b) => parseInt(a) - parseInt(b));
      sequences[chainId] = sortedResNums.map(resNum => residues[resNum]).join('');
      residueNumbers[chainId] = sortedResNums.map(resNum => parseInt(resNum));  // 保存实际的PDB残基编号
    }
    
    console.log('从PDB文件中提取的序列:', sequences);
    console.log('残基编号映射:', residueNumbers);
    return { sequences, residueNumbers };
  };

  // 渲染突变结果
  const renderMutationResults = () => {
    if (!currentTaskResult || !currentTaskResult.mutations) {
      return (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Text type="secondary">暂无突变预测结果</Text>
        </div>
      );
    }

    const { summary, mutations, position_analysis } = currentTaskResult;

    const mutationColumns = [
      { title: '位置', dataIndex: 'position', key: 'position', width: 80 },
      { title: '野生型', dataIndex: 'wildtype', key: 'wildtype', width: 80 },
      { title: '突变型', dataIndex: 'mutation', key: 'mutation', width: 80 },
      { 
        title: 'ΔΔG (kcal/mol)', 
        dataIndex: 'ddG_pred', 
        key: 'ddG_pred', 
        width: 120,
        render: (val: number) => (
          <span style={{ color: val < -1 ? 'green' : val > 1 ? 'red' : 'orange' }}>
            {val.toFixed(2)}
          </span>
        ),
        sorter: (a: MutationData, b: MutationData) => a.ddG_pred - b.ddG_pred
      },
      { 
        title: '分类', 
        dataIndex: 'category', 
        key: 'category', 
        width: 100,
        render: (cat: string) => (
          <Tag color={cat === 'Stabilizing' ? 'green' : cat === 'Destabilizing' ? 'red' : 'orange'}>
            {cat === 'Stabilizing' ? '稳定化' : cat === 'Destabilizing' ? '去稳定化' : '中性'}
          </Tag>
        )
      }
    ];

    const tabItems = [
      {
        key: 'summary',
        label: '总结统计',
        children: summary ? (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Card size="small">
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, fontWeight: 'bold' }}>{summary.total_mutations}</div>
                    <div>总突变数</div>
                  </div>
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small">
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, fontWeight: 'bold', color: summary.mean_ddG < 0 ? 'green' : 'red' }}>
                      {summary.mean_ddG.toFixed(2)}
                    </div>
                    <div>平均ΔΔG</div>
                  </div>
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small">
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, fontWeight: 'bold' }}>{summary.std_ddG.toFixed(2)}</div>
                    <div>标准差</div>
                  </div>
                </Card>
              </Col>
            </Row>
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col span={8}>
                <Card size="small">
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 'bold', color: 'green' }}>{summary.stabilizing_count}</div>
                    <div>稳定化突变</div>
                  </div>
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small">
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 'bold', color: 'orange' }}>{summary.neutral_count}</div>
                    <div>中性突变</div>
                  </div>
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small">
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 'bold', color: 'red' }}>{summary.destabilizing_count}</div>
                    <div>去稳定化突变</div>
                  </div>
                </Card>
              </Col>
            </Row>
            {summary.best_mutation && (
              <Alert
                style={{ marginTop: 16 }}
                message={`最佳稳定化突变: ${summary.best_mutation.wildtype}${summary.best_mutation.position}${summary.best_mutation.mutation} (ΔΔG = ${summary.best_mutation.ddG_pred.toFixed(2)} kcal/mol)`}
                type="success"
                showIcon
              />
            )}
            {summary.worst_mutation && (
              <Alert
                style={{ marginTop: 8 }}
                message={`最不利突变: ${summary.worst_mutation.wildtype}${summary.worst_mutation.position}${summary.worst_mutation.mutation} (ΔΔG = ${summary.worst_mutation.ddG_pred.toFixed(2)} kcal/mol)`}
                type="error"
                showIcon
              />
            )}
          </div>
        ) : null
      },
      {
        key: 'mutations',
        label: `突变详情 (${mutations?.length || 0})`,
        children: (
          <Table
            dataSource={mutations}
            columns={mutationColumns}
            size="small"
            pagination={{ pageSize: 20, showSizeChanger: true }}
            rowKey={(record) => `${record.position}_${record.wildtype}_${record.mutation}`}
          />
        )
      }
    ];

    if (position_analysis && position_analysis.length > 0) {
      const positionColumns = [
        { title: '位置', dataIndex: 'position', key: 'position', width: 80 },
        { title: '野生型', dataIndex: 'wildtype', key: 'wildtype', width: 80 },
        { 
          title: '平均ΔΔG', 
          dataIndex: 'mean_ddG', 
          key: 'mean_ddG', 
          width: 120,
          render: (val: number) => (
            <span style={{ color: val < -1 ? 'green' : val > 1 ? 'red' : 'orange' }}>
              {val.toFixed(2)}
            </span>
          ),
          sorter: (a: any, b: any) => a.mean_ddG - b.mean_ddG
        },
        { 
          title: '最佳突变', 
          key: 'best_mutation', 
          render: (record: any) => (
            <span>{record.best_mutation} ({record.best_ddG.toFixed(2)})</span>
          )
        }
      ];

      tabItems.push({
        key: 'positions',
        label: `位置分析 (${position_analysis.length})`,
        children: (
          <Table
            dataSource={position_analysis}
            columns={positionColumns}
            size="small"
            pagination={{ pageSize: 20, showSizeChanger: true }}
            rowKey="position"
          />
        )
      });
    }

    // Add files tab if file paths are available
    if (currentTaskResult?.files) {
      const { files } = currentTaskResult;
      tabItems.push({
        key: 'files',
        label: '文件信息',
        children: (
          <div>
            <Alert
              message="结果文件已保存到工作空间"
              description="以下文件已保存到服务器，可用于进一步分析"
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Card size="small" title="输入文件">
                  <div>
                    <Text strong>PDB文件: </Text>
                    <Text code>{files.input_pdb}</Text>
                  </div>
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" title="输出文件">
                  <div>
                    <Text strong>CSV结果: </Text>
                    <Text code>{files.output_csv}</Text>
                  </div>
                </Card>
              </Col>
            </Row>
            <Card size="small" title="工作目录" style={{ marginTop: 16 }}>
              <div>
                <Text strong>任务目录: </Text>
                <Text code>{files.task_directory}</Text>
              </div>
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">
                  所有相关文件都保存在此目录中，包括输入PDB文件、预测结果CSV文件等。
                </Text>
              </div>
            </Card>
          </div>
        )
      });
    }

    return (
      <div>
        <div style={{ marginBottom: 16 }}>
          <Text strong>ThermoMPNN 稳定性预测结果</Text>
        </div>
        <Tabs items={tabItems} />
      </div>
    );
  };

  // 任务选择处理
  const handleSelectTask = (taskId: string, checked: boolean) => {
    if (checked) {
      setSelectedTaskIds(prev => [...prev, taskId]);
    } else {
      setSelectedTaskIds(prev => prev.filter(id => id !== taskId));
    }
  };

  // 删除任务
  const handleDeleteTask = async (taskId: string) => {
    try {
      const deletedTasks = JSON.parse(localStorage.getItem('deletedTaskIds') || '[]');
      if (!deletedTasks.includes(taskId)) {
        deletedTasks.push(taskId);
        localStorage.setItem('deletedTaskIds', JSON.stringify(deletedTasks));
      }
      
      setTasks(prevTasks => prevTasks.filter(task => task.id !== taskId));
      message.success('任务已从本地删除');
    } catch (error) {
      console.error('本地删除任务失败:', error);
      message.error('本地删除任务失败');
    }
  };

  // 取消任务
  const handleCancelTask = async (taskId: string) => {
    try {
      await cancelTask(taskId);
      message.success('任务取消成功');
      await refreshTasks();
    } catch (error) {
      console.error('取消任务失败:', error);
      message.error('取消任务失败');
    }
  };

  const uploadProps = { 
    name: 'file', 
    multiple: false, 
    maxCount: 1, 
    accept: '.pdb', 
    beforeUpload: () => false, 
    onChange: handleFileChange 
  } as const;

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/structure')} style={{ marginBottom: 16 }}>
          返回结构与设计
        </Button>
        <Typography>
          <Title level={2}>
            <ExperimentOutlined style={{ marginRight: 8 }} />
            ThermoMPNN 稳定性预测
          </Title>
          <Paragraph>
            使用ThermoMPNN进行蛋白质稳定性预测，分析所有可能的单点突变对蛋白质稳定性的影响(ΔΔG)。
          </Paragraph>
        </Typography>
      </div>

      <Card title="PDB结构文件" style={{ marginBottom: 16 }}>
        <Form form={form} layout="vertical">
          <Form.Item
            name="pdb_file"
            label={
              <span>
                PDB结构文件
                <Tooltip title="上传蛋白质结构的PDB格式文件进行稳定性预测">
                  <InfoCircleOutlined style={{ marginLeft: 8 }} />
                </Tooltip>
              </span>
            }
            rules={[{ required: true, message: '请上传PDB文件' }]}
          >
            <Upload.Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon"><InboxOutlined /></p>
              <p className="ant-upload-text">点击或拖拽PDB文件到此区域上传</p>
              <p className="ant-upload-hint">支持单个PDB文件上传</p>
            </Upload.Dragger>
          </Form.Item>
        </Form>
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="结构预览" style={{ height: 500 }}>
            {pdbData ? (
              <MolStarViewer
                pdbData={pdbData}
                height={420}
                title="蛋白质结构"
              />
            ) : (
              <div
                style={{
                  height: 420,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#f5f5f5',
                  border: '2px dashed #d9d9d9',
                  borderRadius: 6
                }}
              >
                <Text type="secondary">请先上传PDB文件</Text>
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="序列与链信息" style={{ marginBottom: 16 }}>
            {structureInfo ? (
              <Tabs
                size="small"
                items={(structureInfo.chains || []).map((chain) => ({
                  key: chain,
                  label: `链 ${chain} (${structureInfo.sequences?.[chain]?.length || 0} 残基)`,
                  children: (
                    <div
                      style={{
                        background: '#f5f5f5',
                        padding: 12,
                        borderRadius: 4,
                        maxHeight: 200,
                        overflow: 'auto',
                        fontFamily: 'monospace',
                        fontSize: 12
                      }}
                    >
                      {structureInfo.sequences?.[chain] || '未能解析序列'}
                    </div>
                  )
                }))}
              />
            ) : (
              <Alert message="请上传PDB文件以查看序列信息" type="info" showIcon />
            )}
          </Card>

          <Card title="参数设置">
            <Form layout="vertical">
              <Form.Item label="选择分析链">
                <Select
                  value={selectedChain}
                  onChange={setSelectedChain}
                  placeholder="选择要分析的链"
                  disabled={!structureInfo}
                >
                  {(structureInfo?.chains || []).map((chain) => (
                    <Select.Option key={chain} value={chain}>
                      链 {chain} ({structureInfo?.sequences?.[chain]?.length || 0} 残基)
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item label="半胱氨酸突变">
                <Checkbox checked={includeCys} onChange={(e) => setIncludeCys(e.target.checked)}>
                  包含半胱氨酸突变
                </Checkbox>
                <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                  勾选后将包含可能破坏二硫键的半胱氨酸突变
                </div>
              </Form.Item>

              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={submitTask}
                loading={submitting}
                disabled={!file || !structureInfo}
              >
                开始稳定性预测
              </Button>
            </Form>
          </Card>
        </Col>
      </Row>

      {/* 任务列表 */}
      <Card 
        title={`任务列表 (${tasks.length})`}
        extra={
          <Space>
            <Button size="small" icon={<SyncOutlined />} onClick={refreshTasks}>
              刷新任务
            </Button>
            <Button 
              size="small" 
              onClick={() => {
                if (selectedTaskIds.length === 0) {
                  message.warning('请先选择要删除的任务');
                  return;
                }
                const deletedTasks = JSON.parse(localStorage.getItem('deletedTaskIds') || '[]');
                const newDeletedTasks = [...deletedTasks, ...selectedTaskIds];
                localStorage.setItem('deletedTaskIds', JSON.stringify(newDeletedTasks));
                setTasks(prev => prev.filter(task => !selectedTaskIds.includes(task.id)));
                setSelectedTaskIds([]);
                message.success('已批量删除所选任务');
              }}
              disabled={selectedTaskIds.length === 0}
            >
              批量删除 ({selectedTaskIds.length})
            </Button>
          </Space>
        }
      >
        <Table
          dataSource={tasks}
          columns={columns}
          size="small"
          pagination={{ pageSize: 10, showSizeChanger: true }}
          rowKey="id"
        />
      </Card>

      {/* 结果查看Modal */}
      <Modal
        title="ThermoMPNN 预测结果"
        open={viewerVisible}
        onCancel={() => setViewerVisible(false)}
        width={1200}
        footer={null}
        style={{ top: 20 }}
      >
        {currentTaskResult ? (
          <ThermoMPNNResults taskResult={{
            results: [{
              sequence_id: "ThermoMPNN_Analysis",
              // ThermoMPNNResults component treats missing fields defensively
              result: currentTaskResult as any,
              success: true
            }],
            artifacts: {
              files: currentTaskResult.files || {},
              csv_data: (currentTaskResult as any).raw_csv_data || []
            }
          }} />
        ) : (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Text type="secondary">暂无结果数据</Text>
          </div>
        )}
      </Modal>

      {error && (
        <Alert
          message="错误"
          description={error}
          type="error"
          closable
          onClose={() => setError(null)}
          style={{ marginTop: 16 }}
        />
      )}
    </div>
  );
}
